/**
 *
 * Context für die Verwaltung von Warnungen auf Page-Ebene.
 * Ermöglicht es Pages, ihren Warning-Status zu registrieren,
 * der dann in der Navigation (ProjectSidebar) angezeigt wird.
 *
 * Unterstützt auch API-first Validierungen: Wenn ein Backend-Endpoint
 * Validierungen zurückgibt, können diese direkt in den Context übernommen werden.
 *
 * Siehe ADR: docs/adr/001_cross_page_validations.md
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  PageType,
  ValidationsResponse,
  ValidationEntry,
  DOMAIN_TO_PAGE_TYPE,
} from "../types";

/** Stabile leere Array-Referenz (verhindert unnötige Re-Renders bei React.memo) */
const EMPTY_VALIDATION_ENTRIES: ValidationEntry[] = [];

interface PageWarningsContextType {
  /** Map von PageType zu Warning-Status */
  pageWarnings: Map<PageType, boolean>;
  /** Map von PageType zu Validierungseinträgen (für detaillierte Tooltips) */
  validationEntries: Map<PageType, ValidationEntry[]>;
  setPageWarning: (pageType: PageType, hasWarning: boolean) => void;
  hasWarning: (pageType: PageType) => boolean;
  /** Verarbeitet Validierungen aus einer API-Response */
  setValidationsFromAPI: (validations: ValidationsResponse) => void;
  /** Holt alle Validierungseinträge für eine Page */
  getValidationEntries: (pageType: PageType) => ValidationEntry[];
}

const PageWarningsContext = createContext<PageWarningsContextType | undefined>(
  undefined,
);

interface PageWarningsProviderProps {
  children: React.ReactNode;
}

/**
 * Wird im ProjectLayout verwendet
 */
export const PageWarningsProvider: React.FC<PageWarningsProviderProps> = ({
  children,
}) => {
  const [warnings, setWarnings] = useState<Map<PageType, boolean>>(new Map());
  const [validationEntries, setValidationEntries] = useState<
    Map<PageType, ValidationEntry[]>
  >(new Map());

  // WICHTIG: setPageWarning prüft ob sich der Wert wirklich ändert
  // um unnötige Re-Renders zu vermeiden
  const setPageWarning = useCallback(
    (pageType: PageType, hasWarning: boolean) => {
      setWarnings((prev) => {
        const currentValue = prev.get(pageType) ?? false;
        // Nur updaten wenn sich der Wert wirklich ändert
        if (currentValue === hasWarning) {
          return prev; // Gleiche Referenz = kein Re-Render
        }
        const newWarnings = new Map(prev);
        if (hasWarning) {
          newWarnings.set(pageType, true);
        } else {
          newWarnings.delete(pageType);
        }
        return newWarnings;
      });
    },
    [],
  );

  const hasWarning = useCallback(
    (pageType: PageType) => {
      return warnings.get(pageType) ?? false;
    },
    [warnings],
  );

  /**
   * Verarbeitet Validierungen aus einer API-Response.
   *
   * Aktualisiert werden ausschließlich die Seiten der Domänen, die die Response als
   * geprüft ausweist (`validations.domains`). Eine Antwort aus der Finanzierung lässt
   * damit die Tarif-Warnungen unberührt, statt sie mangels Einträgen zu löschen.
   */
  const setValidationsFromAPI = useCallback(
    (validations: ValidationsResponse) => {
      const entriesByPage = new Map<PageType, ValidationEntry[]>();

      const allEntries = [...validations.warnings, ...validations.errors];

      for (const entry of allEntries) {
        const pageType = DOMAIN_TO_PAGE_TYPE[entry.domain];
        if (pageType) {
          const existing = entriesByPage.get(pageType) ?? [];
          entriesByPage.set(pageType, [...existing, entry]);
        }
      }

      const geprueftePages = (validations.domains ?? []).map(
        (domain) => DOMAIN_TO_PAGE_TYPE[domain],
      );

      setValidationEntries((prev) => {
        const next = new Map(prev);
        for (const pageType of geprueftePages) {
          const entries = entriesByPage.get(pageType);
          if (entries?.length) {
            next.set(pageType, entries);
          } else {
            next.delete(pageType);
          }
        }
        return next;
      });

      setWarnings((prev) => {
        const next = new Map(prev);
        for (const pageType of geprueftePages) {
          if ((entriesByPage.get(pageType)?.length ?? 0) > 0) {
            next.set(pageType, true);
          } else {
            next.delete(pageType);
          }
        }
        return next;
      });
    },
    [],
  );

  // Holt alle Validierungseinträge für eine Page
  // Gibt EMPTY_VALIDATION_ENTRIES zurück wenn keine vorhanden (stabile Referenz für React.memo)
  const getValidationEntries = useCallback(
    (pageType: PageType): ValidationEntry[] => {
      return validationEntries.get(pageType) ?? EMPTY_VALIDATION_ENTRIES;
    },
    [validationEntries],
  );

  const value = useMemo(
    () => ({
      pageWarnings: warnings,
      validationEntries,
      setPageWarning,
      hasWarning,
      setValidationsFromAPI,
      getValidationEntries,
    }),
    [
      warnings,
      validationEntries,
      setPageWarning,
      hasWarning,
      setValidationsFromAPI,
      getValidationEntries,
    ],
  );

  return (
    <PageWarningsContext.Provider value={value}>
      {children}
    </PageWarningsContext.Provider>
  );
};

/**
 * Wird von Pages verwendet (z.B. TariffsPage)
 *
 * WICHTIG: Verwendet nur die stabilen Funktionen aus dem Context,
 * um Endlosschleifen zu vermeiden.
 */
export const usePageWarning = (pageType: PageType) => {
  const context = useContext(PageWarningsContext);

  // Extrahiere die stabile setPageWarning Funktion direkt
  // (nicht das ganze context Objekt, das sich bei jedem Update ändert)
  const setPageWarning = context?.setPageWarning;

  // Stabile setWarning Funktion - nur von setPageWarning und pageType abhängig
  const setWarning = useCallback(
    (hasWarning: boolean) => {
      setPageWarning?.(pageType, hasWarning);
    },
    [setPageWarning, pageType],
  );

  // hasWarning separat berechnen (darf sich ändern, ist aber kein Dependency-Problem)
  const currentHasWarning = context?.pageWarnings.get(pageType) ?? false;

  return {
    setWarning,
    hasWarning: currentHasWarning,
  };
};

/**
 * Wird von ProjectSidebar verwendet
 */
export const usePageWarnings = () => {
  const context = useContext(PageWarningsContext);

  if (context === undefined) {
    return {
      pageWarnings: new Map<PageType, boolean>(),
      hasWarning: (_pageType: PageType) => false,
      setValidationsFromAPI: (_validations: ValidationsResponse) => {},
      getValidationEntries: (_pageType: PageType) => EMPTY_VALIDATION_ENTRIES,
    };
  }

  return {
    pageWarnings: context.pageWarnings,
    hasWarning: context.hasWarning,
    setValidationsFromAPI: context.setValidationsFromAPI,
    getValidationEntries: context.getValidationEntries,
  };
};

export default PageWarningsContext;
